import { DragIndicator, ExpandLess, ExpandMore, Help } from '@mui/icons-material';
import {
    Box,
    Collapse,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { StepDataType } from '../../../../../../dtos';

export interface SmartValueList {
    name: string;
    items: SmartValueListItem[];
}

export interface SmartValueListItem {
    displayName: string;
    value: string;
    metadata: {
        source: SmartValueSource;
        type: StepDataType;
        isRequired: boolean;
        isFutureDate?: boolean;
    }
}

export enum SmartValueSource {
    PREVIOUS_STEP,
    CURRENT_STEP,
    PLAY,
    JOB_FUNCTION
}

interface ISmartValuesSliceRowProps {
    smartValueList: SmartValueList;
    defaultOpen?: boolean;
}

const SmartValueSliceRow: FC<ISmartValuesSliceRowProps> = ({ smartValueList, defaultOpen = false }) => {
    const { name, items } = smartValueList;
    const [open, setOpen] = useState(defaultOpen);

    return (
        <List key={name} disablePadding sx={{ background: '#fff' }} dense>
            <ListItemButton divider onClick={() => setOpen(!open)}>
                <ListItemText primary={name} primaryTypographyProps={{ fontWeight: 'bold' }} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={open} timeout='auto' unmountOnExit>
                <List disablePadding dense>
                    {items.length === 0 && (
                        <ListItem>
                            <ListItemText primary='No Data Collected So Far' />
                        </ListItem>
                    )}
                    {items.map((item) => (
                        <ListItem
                            key={item.value}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', `{{${item.value}}}`);
                                const preview = document.createElement('div');
                                preview.style.maxWidth = '200px';
                                preview.style.textAlign = 'center';
                                preview.innerText = `${name} ${item.displayName}`;
                                document.body.appendChild(preview);
                                e.dataTransfer.setDragImage(preview, 8, 20);
                                setTimeout(() => document.body.removeChild(preview), 0);
                            }}
                            sx={{ cursor: 'grab' }}>
                            <ListItemText primary={item.displayName} />
                            <DragIndicator />
                        </ListItem>
                    ))}
                </List>
            </Collapse>
        </List>
    );
};

export interface ISmartValueSliceProps {
    smartValueSections: SmartValueList[];
}

export const SmartValueSlice: FC<ISmartValueSliceProps> = ({ smartValueSections }) => {
    return (
        <Grid item height='100%' direction='column' width='200px' sx={{ background: '#adcbee' }}>
            <Typography
                fontSize='0.875rem'
                fontWeight='500'
                display='flex'
                justifyContent='center'
                alignItems='center'
                gap={0.5}
                p={1.7}
                height='49px'
                borderBottom='1px solid rgba(0, 0, 0, .2)'>
                DATA COLLECTED
                <Tooltip title='These are the data values that have been collected by previous steps in this play.'>
                    <Help fontSize='inherit' />
                </Tooltip>
            </Typography>
            <Box sx={{ maxHeight: '90%', maxWidth: '100%', overflowY: 'auto', p: 1 }}>
                {smartValueSections.map((section, index) => (
                    <SmartValueSliceRow key={section.name} smartValueList={section} defaultOpen={index === 0}/>
                ))}
            </Box>
        </Grid>
    );
};
